import useResizeObserver from '@react-hook/resize-observer';
import {
    DropdownDataSource,
    DropdownFilterConfig,
    DropdownPopup,
    DropdownResultsRenderer,
    HighlightedIndex,
    TrailingItem,
} from 'components/dropdown/DropdownPopup';
import {
    useDropdownControl,
    UseDropdownControlResult,
} from 'components/dropdown/useDropdownControl';
import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import {
    Dispatch,
    ReactNode,
    Ref,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { mergeRefs, updateRef } from 'utils/mergeRefs';

export type DropdownTriggerRenderInfo = Readonly<{
    /**
     * The current opened/closed state of the popup.
     */
    isPopupOpen: boolean;
    /**
     * Setter to open/close the popup.
     */
    setIsPopupOpen: Dispatch<React.SetStateAction<boolean>>;
    onBlur: React.FocusEventHandler;
}>;

export type DropdownTriggerRenderer = (info: DropdownTriggerRenderInfo) => ReactNode;

export type BaseDropdownPopupOptions = Readonly<{
    /**
     * Aria label of the popup.
     */
    ariaLabel?: string;
    /**
     * Width of popup.
     */
    width?: string;
    /**
     * Max height of popup.
     * @default '60vh'
     */
    maxHeight?: string;
    /**
     * Callback for when popup is opened.
     */
    onOpen?: () => void;
    /**
     * Callback for when popup is closed.
     */
    onClose?: () => void;
    /**
     * Ref to the popup element when not in full screen mode.
     */
    popupRef?: Ref<HTMLDivElement>;
    /**
     * Defines the container to which the popup will be appended. Defaults to 'body'
     */
    appendTo?: HTMLElement | null;
}>;

export type BaseDropdownProps<TData> = Readonly<{
    /**
     * Callback to render the trigger that opens the dropdown.
     */
    renderTrigger: DropdownTriggerRenderer;
    /**
     * Filtering config.
     */
    filterable?: DropdownFilterConfig;
    /**
     * The data to be rendered (can be an array or a dropdown data source object).
     */
    data: TData[] | DropdownDataSource<TData> | undefined;
    /**
     * Callback for rendering dropdown results.
     */
    renderResults: DropdownResultsRenderer<TData>;
    /**
     * Callback for when a result is selected.
     */
    onSelect: (item: TData) => void;
    /**
     * Ref to gain access to dropdown internal state.
     */
    controlRef?: Ref<UseDropdownControlResult>;
    /**
     * Dropdown popup configuration.
     */
    popup?: BaseDropdownPopupOptions;
    /**
     * Additional content refs.
     */
    triggerRef: RefObject<HTMLElement>;
    TrailingItemComponent?: TrailingItem;
}>;

export function BaseDropdown<TData>({
    renderTrigger,
    filterable,
    data,
    renderResults,
    onSelect,
    controlRef,
    popup,
    triggerRef,
    TrailingItemComponent,
}: BaseDropdownProps<TData>) {
    const [highlightedIndex, setHighlightedIndex] = useState<HighlightedIndex>(0);
    const popupRef = useRef<HTMLDivElement>(null);

    const onLoadMore = () => {
        if (data !== undefined && !Array.isArray(data)) {
            data.onLoadMore();
        }
    };

    const control = useDropdownControl({
        onOpen: popup?.onOpen,
        onClose: popup?.onClose,
        onLoadMore,
    });
    const { isPopupOpen, setIsPopupOpen } = control;

    const trigger = useMemo(
        () =>
            renderTrigger({
                isPopupOpen,
                setIsPopupOpen,
                onBlur: (e) => {
                    if (
                        // only if clicked-on elem wasn't inside the popup
                        (!e.relatedTarget ||
                            !popupRef.current?.contains(e.relatedTarget)) &&
                        (!filterable || e.target !== triggerRef.current)
                    ) {
                        filterable?.onChange('');
                        setIsPopupOpen(false);
                    }
                },
            }),
        [filterable, isPopupOpen, renderTrigger, setIsPopupOpen, triggerRef]
    );

    const closePopup = useCallback(() => {
        // clear the filter text on close
        filterable?.onChange('');
        // its important that we give focus before a potentially-focused
        // dropdown item is removed from the dom
        triggerRef.current?.focus();
        setIsPopupOpen(false);
        window.setTimeout(() => triggerRef.current?.focus());
    }, [setIsPopupOpen, triggerRef, filterable]);

    const onItemSelect = useCallback(
        (item: TData) => {
            onSelect(item);
            closePopup();
        },
        [closePopup, onSelect]
    );

    useEffect(() => {
        updateRef(controlRef, control);
    }, [control, controlRef]);

    // Close popup when user clicks outside of popup or whatever the popup is anchored to
    useOnClickOutside(
        [popupRef, triggerRef],
        () => {
            setIsPopupOpen(false);
        },
        isPopupOpen
    );

    const triggerSize = useElementSize(triggerRef);
    // Use supplied width or default to the width of the parent element.
    const finalWidth = useMemo(
        () =>
            popup?.width ??
            (triggerSize !== undefined ? `${triggerSize.width}px` : undefined),
        [popup?.width, triggerSize]
    );

    return (
        <>
            {trigger}
            {isPopupOpen && (
                <>
                    {/* it takes one render for sizing to report a value */}
                    {finalWidth && (
                        <DropdownPopup<TData>
                            ariaLabel={popup?.ariaLabel}
                            popupRef={mergeRefs(popup?.popupRef, popupRef)}
                            anchorRef={triggerRef}
                            filterable={filterable}
                            data={data}
                            renderResults={renderResults}
                            highlightedIndex={highlightedIndex}
                            onHighlightedIndexChange={setHighlightedIndex}
                            onClose={closePopup}
                            onSelect={onItemSelect}
                            width={finalWidth}
                            maxHeight={popup?.maxHeight}
                            appendTo={popup?.appendTo}
                            TrailingItemComponent={TrailingItemComponent}
                        />
                    )}
                </>
            )}
        </>
    );
}

type Rect = {
    width: number;
    height: number;
};
const useElementSize = (target: React.RefObject<HTMLElement>): Rect | undefined => {
    const [size, setSize] = useState<Rect | undefined>(undefined);

    useLayoutEffect(() => {
        if (target.current) {
            setSize(target.current.getBoundingClientRect());
        }
    }, [target]);

    useResizeObserver(target, (entry) => {
        const borderBoxSize = entry.borderBoxSize[0];
        setSize({ width: borderBoxSize.inlineSize, height: borderBoxSize.blockSize });
    });
    return size;
};
